import React, { useMemo } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import Moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";

import {
  createColumnHelper,
} from "@tanstack/react-table";

function WithholdingList({
  data,
  isLoading,
  isAuthenticated,
  controlType,
  saveRecord,
  deleteRecord,
  updateTableState,
  userPermissions,
  user,
  bulkSaveRecords,
  refreshRecords,
  filter,
  pageSize = 25,
  withholdingReasons,
}) {

  const dataSet = 'withholdings';
  const createId = useMemo(() => createElementIdFactory("withholding-list"), []);
  const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);
  const hasTrustProof =
    userPermissions?.trust_proof === true ||
    userPermissions?.userPermissions?.trust_proof === true;

  // --- TanStack Table Setup ---
  const tableData = data || [];
  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("selected", {
      id: "selected",
      header: "",
      cell: EditableCell,
      size: 60,
      meta: {
        style: { textAlign: "center" },
      },
      enableSorting: false,
    }),
    columnHelper.accessor("property_key", {
      id: "property_key",
      header: "Cityscope Ref",
      cell: (info) => (
        <div>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={getPropertiesLink(info.getValue())}
          >
            {info.getValue()}
          </a>
        </div>
      ),
      size: 60,
    }),
    columnHelper.accessor("withholding_reason", {
      id: "withhold_reason",
      header: "Reason",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("withholding_text", {
      id: "withholding_text",
      header: "Additional Comments",
      cell: EditableCell,
    }),
    columnHelper.accessor("primary_classification", {
      id: "primary_classification",
      header: "Primary Classification",
      cell: (info) => info.getValue(),
      size: 120,
    }),
    columnHelper.accessor("link_id", {
      id: "link_id",
      header: "Link",
      cell: (info) => (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`${info.row.original.link}`}
        >
          {info.getValue()}
        </a>
      ),
      size: 200,
    }),
    columnHelper.accessor(
      (d) => {
        if (d.hold_date) {
          return Moment(d.hold_date).local().format("DD-MM-YYYY");
        } else {
          return null;
        }
      },
      {
        id: "hold_date",
        header: "Hold Date",
        cell: (info) => info.getValue(),
        size: 200,
      }
    ),
    columnHelper.accessor("scoped_by", {
      id: "scoped_by",
      header: "Hold By",
      cell: (info) => info.getValue(),
      size: 75,
    }),
    columnHelper.accessor("released_date", {
      id: "released_date",
      header: "Release Date",
      cell: (info) => {
        const value = info.getValue();
        if (value) {
          return Moment(value).local().format("DD-MM-YYYY");
        } else {
          return null;
        }
      },
      size: 150,
    }
    ),
    columnHelper.accessor("released_by", {
      id: "released_by",
      header: "Released By",
      cell: (info) => info.getValue(),
      size: 60,
    }),
    columnHelper.accessor("property_withholding_id", {
      id: "property_withholding_id",
      header: "",
      cell: ({ row }) =>
        <Button
          type="button"
          size="sm"
          variant="danger"
          style={{ whiteSpace: "nowrap" }}
          disabled={!hasTrustProof || (row.original.link_id != null && row.original.primary_classification !== "Residential" && row.original.primary_classification !== "Car Park")}
          onClick={() => handleReleaseRecord(row)}
        >
          Remove Hold
        </Button>,
      size: 150,
      enableSorting: false,
    }),
  ];

  // --- Data Fetching and Update Functions ---
  // useEffect(() => {
  //   setIsLoading(true);
  //   if (!isAuthenticated) {
  //     return;
  //   }
  //   if (userPermissions) {
  //     console.log("In witholding list and have user permissions props");
  //     if (userPermissions.trust_proof) {
  //       setTrust_proof(true);
  //     } else {
  //       setTrust_proof(false);
  //     }
  //   }
  // }, [isAuthenticated, userPermissions, filter]);


  function selectAll(event) {
    if (!Array.isArray(tableData)) {
      return;
    }

    const nextData = tableData.map((row) => ({
      ...row,
      selected: true,
      modified: true,
    }));

    updateTableState(nextData, dataSet);
  }


  function deselectAll(event) {
    if (!Array.isArray(tableData)) {
      return;
    }

    const nextData = tableData.map((row) => ({
      ...row,
      selected: false,
      modified: true,
    }));

    updateTableState(nextData, dataSet);
  }


  async function handleReleaseRecord(row) {
    const r = window.confirm(
      "Please confirm you have proofed this Property and would like to remove the HOLD status?"
    );
    
    if (r === true) {
      const updatedRecord = {
        ...row.original,
        released_date: new Date().toISOString(),
        released_by: user,
        last_edited_by: user,
      };

      try {
        const result =
          saveRecord.length >= 2
            ? await saveRecord(dataSet, updatedRecord)
            : await saveRecord(updatedRecord);
        if (result && result.result !== false) {
          const nextData = tableData.filter(
            (item) => String(item.property_withholding_id) !== String(row.original?.property_withholding_id)
          );
          updateTableState?.(nextData, dataSet);
          if (typeof refreshRecords === "function") {
            await refreshRecords();
          }
        } else {
          window.alert("Failed to remove hold: " + (result?.error_message || "Unknown error"));
        }
      } catch (error) {
        console.error("Error releasing hold:", error);
        window.alert("Error removing hold. Please try again.");
      }
    }
  }

  async function handleReleaseAllRecord(event) {
    console.log("Remove withholding bulk");
    let localdata = data.filter((a) => {
      if (
        a.selected &&
        (a.link_id === null ||
          a.primary_classification === "Residential" ||
          a.primary_classification === "Car Park")
      ) {
        return true;
      } else {
        return false;
      }
    });

    var r = window.confirm(
      "Please confirm you have proofed the selected Properties and would like to remove the HOLD status of each? Note: Selected HOLD records with a Link ID may be excluded from this batch update."
    );

    if (r === true) {
      for (var i = localdata.length - 1; i >= 0; i--) {
        localdata[i].released_date = new Date().toISOString();
        localdata[i].released_by = user;
        localdata[i].last_edited_by = user;
      }
      if (typeof bulkSaveRecords === "function") {
        await bulkSaveRecords(localdata);
      } else {
        for (const record of localdata) {
          if (saveRecord.length >= 2) {
            await saveRecord(dataSet, record);
          } else {
            await saveRecord(record);
          }
        }
      }

      const releasedIds = new Set(localdata.map((item) => String(item.property_withholding_id)));
      const nextData = tableData.filter(
        (item) => !releasedIds.has(String(item.property_withholding_id))
      );
      updateTableState?.(nextData, dataSet);

      if (typeof refreshRecords === "function") {
        await refreshRecords();
      }
    }
  }

  // async function handleReleaseRecord(cellInfo, event) {
  //   event.preventDefault();
  //   console.log("Remove withholding");
  //   let localdata = [...data];

  //   var r = window.confirm(
  //     "Please confirm you have proofed the selected Properties and would like to remove the HOLD status of this Property?"
  //   );
  //   if (r === true) {
  //     console.log(cellInfo);

  //     localdata[cellInfo.index].released_date = new Date();
  //     localdata[cellInfo.index].released_by = user;
  //     await handleDataSave(localdata[cellInfo.index]).then((result) => {
  //       localdata.splice(cellInfo.index, 1);
  //       // updateTableState(localdata, dataSet);
  //     });
  //   }
  // }

  // async function addRecord(event) {
  //   let localdata = [...data];

  //   //Update the following with the minimum details to create a record (all not nulls populated)
  //   //Requires updating per dataset
  //   localdata.unshift({
  //     withholding_reason_id: null,
  //     withholding_text: null,
  //     link: null,
  //     property_id: filter.propertyid,
  //     created_by: user,
  //     last_edited_by: user,
  //     new_record: true,
  //   });
  //   // updateTableState(localdata, dataSet);
  // }

  function getPropertiesLink(cityscopeReference) {
    const localselectedProperties = data
      .filter((a) => a.selected || a.property_key === cityscopeReference)
      .map((a) => a.property_key);
    if (localselectedProperties.length === 0)
      return `../properties/${cityscopeReference}`;
    return `../properties/${cityscopeReference}?browselist=${localselectedProperties.join(
      ","
    )}`;
  }

  // --- Render Functions ---
  function renderComponent() {
    const activeTable = tableData;
    const returnedCount = Array.isArray(data) ? data.length : 0;
    const firstResultCount =
      Array.isArray(data) && data.length > 0 ? data[0]?.result_count : null;
    const totalCount =
      firstResultCount !== null && firstResultCount !== undefined && firstResultCount !== ""
        ? firstResultCount
        : returnedCount;

    return (
      <div className="holdings">
        {controlType === "table" && (
          <Row className="show-Container" style={{ marginTop: '12px' }}>
            <Col>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Button
                  variant="primary"
                  type="button"
                  size="sm"
                  onClick={selectAll}
                >
                  Select All
                </Button>
                <Button
                  variant="primary"
                  type="button"
                  size="sm"
                  onClick={deselectAll}
                >
                  Deselect All
                </Button>
                {!isLoading && hasTrustProof && (
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    onClick={() => handleReleaseAllRecord()}
                  >
                    Remove HOLD on Selected
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <b>
              {returnedCount} of {totalCount}{" "}
              records returned.
            </b>
          </Col>
        </Row>
        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : (
              <GenericTable
                data={activeTable}
                columns={columns}
                section={dataSet}
                elementIds={tableElementIds}
                pageSize={pageSize}
                enablePagination={true}
                enableOriginalOrderSort={true}
                meta={{
                  updateData: (rowIndex, columnId, value) => {
                    updateTableState(dataSet, rowIndex, columnId, value);
                  },
                  handleSave: async ({ row, column, value }) => {
                    if (column?.id === "selected") {
                      return true;
                    }

                    const record = {
                      ...row.original,
                      ...(column?.id ? { [column.id]: value } : {}),
                    };
                    try {
                      if (saveRecord.length >= 2) {
                        await saveRecord(dataSet, record);
                      } else {
                        await saveRecord(record);
                      }
                      console.log("Record saved successfully via parent logic");
                    } catch (err) {
                      console.error("Save failed:", err);
                    }
                  },
                  handleDelete: ({ row }) => {
                    deleteRecord(dataSet, row.original);
                  }
                }}
                rowClassName={(row) => (row.index % 2 === 0 ? "even" : "odd")}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="WithholdingSection">
      {isAuthenticated ? renderComponent() : console.log("User not authenticated.")}
    </div>
  );
}

export default WithholdingList;